import React, { useEffect, useState } from 'react';
import { Fade } from 'react-awesome-reveal';
import { PaymentForm } from '../PaymentForm';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { renderAgreementTerms } from '../../shared/render-agreement-terms';
import { usePaymentError } from '../../shared/use-payment-error-modal';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { getSubscriptionStatus } from '../../../lib/use-cases/get-subscription-status';
import styles from './Checkout.css';

function Checkout({ subscriptionData, merchantData, onClose, tryAgain, isPending, promoType, defaultDiscountValue = 50, successUrl, termsOfUseLink = '', isWalletEnabled = false, isPeriodInDays = false }) {
    const displayDiscount = subscriptionData.displayDiscountPercent ?? defaultDiscountValue;
    const isDiscountShown = Boolean(promoType) && displayDiscount > 0;

    const [failedCard, setFailedCard] = useState(null);

    const fetchSubscriptionStatus = async () => {
        if (!merchantData.subscriptionId) return;

        const status = await getSubscriptionStatus(merchantData.subscriptionId);

        if (status?.card) {
            setFailedCard(status.card);
        }
    };

    useEffect(() => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        logEvent(EVENT_TYPES.SALE_CHECKOUT_PAGE_VISITED);
        fetchSubscriptionStatus();
    }, []);

    const { errorData, setErrorData, handleOnTryAgain } = usePaymentError(async () => {
        await tryAgain(subscriptionData, merchantData.subscriptionId);
        fetchSubscriptionStatus();
    });

    const renderTotalRow = () => {
        if (subscriptionData.trial?.billingPeriod) {
            const billingPeriodValue = subscriptionData.trial.billingPeriod.value;

            return (
                <div className={styles.totalRow}>
                    <span>After {billingPeriodValue} {subscriptionData.trial.billingPeriod.unit}{billingPeriodValue === 1 ? '' : 's'} (auto-renews):</span>
                    <span>{subscriptionData.price}</span>
                </div>
            );
        }

        return (
            <div className={styles.totalRow}>
                <span>
                    After {subscriptionData.billingPeriod.value}{' '}
                    {subscriptionData.billingPeriod.unit}{subscriptionData.billingPeriod.value === 1 ? '' : 's'} (auto-renews):
                </span>
                <span>{subscriptionData.price}</span>
            </div>
        );
    };

    if (errorData.isModalVisible) {
        return (
            <Fade
                duration={500}
                triggerOnce
            >
                <div className={`${styles.root} ${styles.error}`}>
                    <div className={styles.header}>
                        <h1 className={styles.title}>
                            {errorData.title}
                        </h1>
                        <Button
                            type='secondary'
                            iconLeft={{ name: 'close' }}
                            className={styles.buttonClose}
                            onClick={onClose}
                            buttonProps={{ 'aria-label': 'Close' }}
                        />
                    </div>
                    <div className={styles.noticeBlock}>
                        <Icon name='info' />
                        <p>{errorData.description}</p>
                    </div>
                    <div className={styles.helpBlock}>
                        <p>What to do:</p>
                        <p>{errorData.helpDescription}</p>
                    </div>
                    <div className={styles.buttons}>
                        <Button
                            label='Try again'
                            onClick={handleOnTryAgain}
                            isLoading={isPending}
                        />
                        <Button
                            label='Decline'
                            onClick={onClose}
                            type='secondary'
                        />
                    </div>
                </div>
            </Fade>
        );
    }

    return (
        <Fade
            duration={500}
            triggerOnce
        >
            <div className={styles.root}>
                <div className={styles.header}>
                    <h1 className={styles.title}>
                        Complete your checkout
                    </h1>
                    <Button
                        type='secondary'
                        iconLeft={{ name: 'close' }}
                        className={styles.buttonClose}
                        onClick={onClose}
                        buttonProps={{ 'aria-label': 'Close' }}
                    />
                </div>
                <div className={styles.wrapper}>
                    <div className={styles.summaryBlock}>
                        {isDiscountShown ? (
                            <>
                                <h2>Your Order Summary</h2>
                                <div className={styles.row}>
                                    <span>Regular {subscriptionData.summaryBillingPeriod.value}-{subscriptionData.summaryBillingPeriod.unit} price</span>
                                    <span>{subscriptionData.displayOldPrice ?? subscriptionData.summaryPrice}</span>
                                </div>
                                <div className={styles.row}>
                                    <span>Your {displayDiscount}% discount</span>
                                    <span className={styles.discountValue}>-{subscriptionData.discountPrice}</span>
                                </div>
                                <div className={styles.divider} />
                                <div className={styles.totalRow}>
                                    <span>Total today:</span>
                                    <span>{subscriptionData.trial?.price || subscriptionData.summaryPrice}</span>
                                </div>
                                {Boolean(subscriptionData.trial) && (
                                    <div className={styles.summarySaveBlock}>
                                        <Icon name='fire' />
                                        You save {subscriptionData.discountPrice} in first {subscriptionData.summaryBillingPeriod.value === 1 ? '' : subscriptionData.billingPeriod.value} {subscriptionData.billingPeriod.unit} ({displayDiscount}% OFF)
                                    </div>
                                )}
                                {renderTotalRow()}
                            </>
                        ) : (
                            <>
                                <div className={styles.totalRow}>
                                    <span>Total today:</span>
                                    <span>{subscriptionData.trial?.price || subscriptionData.summaryPrice}</span>
                                </div>
                                <div className={styles.divider} />
                                {renderTotalRow()}
                            </>
                        )}
                        <p className={styles.agreementTermsText}>{renderAgreementTerms(subscriptionData, 'By selecting a payment method and proceeding', termsOfUseLink, isPeriodInDays)}</p>
                    </div>
                    <PaymentForm
                        merchantData={merchantData}
                        setErrorData={setErrorData}
                        successUrl={successUrl}
                        plan={subscriptionData.plan}
                        discount={promoType ? displayDiscount : 0}
                        failedCard={failedCard}
                        isWalletEnabled={isWalletEnabled}
                    />
                </div>
            </div>
        </Fade>
    );
}

export {
    Checkout
};
